import { useNavigate } from "react-router-dom";
import { Zap, Cpu, Layers, Wrench, BatteryCharging, CheckCircle2, ShieldCheck, Phone, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { SITE, IMAGES } from "@/data/site";

const SERVICE_ICONS = { electrical: Zap, automation: Cpu, panels: Layers, maintenance: Wrench, power: BatteryCharging };

export default function Services() {
  const { t } = useLang();
  const navigate = useNavigate();

  const enquire = (title) => navigate("/contact", { state: { service: title } });

  return (
    <div>
      {/* Header banner */}
      <section className="relative bg-navy text-white overflow-hidden" data-testid="services-header">
        <div className="absolute inset-0 grid-pattern-dark opacity-60" />
        <div className="absolute -top-24 right-0 w-[420px] h-[420px] rounded-full bg-electric/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-sky-300 font-semibold">{t.servicesPage.eyebrow}</p>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight" data-testid="services-title">{t.servicesPage.title}</h1>
            <p className="mt-4 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">{t.servicesPage.subtitle}</p>
          </Reveal>
        </div>
      </section>

      {/* Catalog */}
      <section className="py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8" data-testid="services-catalog-grid">
            {t.servicesPage.items.map((s, i) => {
              const Icon = SERVICE_ICONS[s.id];
              return (
                <Reveal key={s.id} delay={i * 0.08} className="h-full">
                  <div
                    className={`h-full rounded-2xl border border-slate-200 bg-white p-8 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 ${
                      i === 0 ? "lg:col-span-2" : ""
                    }`}
                    data-testid={`service-card-${s.id}`}
                  >
                    {i === 0 && (
                      <div className="overflow-hidden rounded-xl mb-7 aspect-[21/9]">
                        <img
                          src={IMAGES.factory_wires}
                          alt={s.title}
                          loading="lazy"
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div className={`flex ${i === 0 ? "flex-col lg:flex-row lg:items-center lg:gap-10" : "flex-col"}`}>
                      <div className={i === 0 ? "lg:max-w-md" : ""}>
                        <span className="w-14 h-14 rounded-xl bg-blue-50 text-electric flex items-center justify-center">
                          <Icon className="w-7 h-7" />
                        </span>
                        <h2 className="mt-5 font-display text-xl sm:text-2xl font-semibold tracking-tight text-navy">{s.title}</h2>
                        <p className="mt-3 text-sm sm:text-base text-slate-500 leading-relaxed">{s.desc}</p>
                      </div>
                      <div className={i === 0 ? "mt-6 lg:mt-0 lg:flex-1" : "mt-5"}>
                        <ul className="space-y-2.5">
                          {s.features.map((f, j) => (
                            <li key={j} className="flex items-start gap-2.5 text-sm text-slate-700">
                              <CheckCircle2 className="w-5 h-5 mt-0.5 text-electric shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                        <button
                          onClick={() => enquire(s.title)}
                          data-testid={`service-quote-trigger-${s.id}`}
                          className="mt-6 inline-flex items-center gap-2 rounded-full bg-electric px-6 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 active:scale-[0.98] transition-all duration-200"
                        >
                          {t.servicesPage.enquire}
                        </button>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Home & smart services */}
      <section className="py-16 sm:py-20 lg:py-24 bg-slate-50 grid-pattern-light" data-testid="home-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-electric font-semibold">{t.homeServices.eyebrow}</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">{t.homeServices.title}</h2>
            <p className="mt-3 text-base text-slate-500 max-w-xl">{t.homeServices.subtitle}</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-services-grid">
            {t.homeServices.items.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.07} className="h-full">
                <div
                  className="group h-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300"
                  data-testid={`home-service-card-${s.id}`}
                >
                  <div className="overflow-hidden aspect-[16/10]">
                    <img
                      src={IMAGES[s.img]}
                      alt={s.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-lg font-semibold text-navy">{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-500 leading-relaxed">{s.desc}</p>
                    <button
                      onClick={() => enquire(s.title)}
                      data-testid={`home-service-quote-trigger-${s.id}`}
                      className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-electric hover:gap-3 transition-all duration-200"
                    >
                      {t.homeServices.bookNow}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Office & communication */}
      <section className="py-16 sm:py-20 lg:py-24" data-testid="office-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-electric font-semibold">{t.extraServices.eyebrow}</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">{t.extraServices.title}</h2>
            <p className="mt-3 text-base text-slate-500 max-w-xl">{t.extraServices.subtitle}</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="office-services-grid">
            {t.extraServices.items.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.07} className="h-full">
                <div
                  className="group h-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300"
                  data-testid={`office-service-card-${s.id}`}
                >
                  <div className="overflow-hidden aspect-[16/7]">
                    <img
                      src={IMAGES[s.img]}
                      alt={s.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6 sm:p-7">
                    <h3 className="font-display text-lg font-semibold text-navy">{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-500 leading-relaxed">{s.desc}</p>
                    <button
                      onClick={() => enquire(s.title)}
                      data-testid={`office-service-quote-trigger-${s.id}`}
                      className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-electric hover:gap-3 transition-all duration-200"
                    >
                      {t.extraServices.bookNow}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Small jobs */}
      <section className="py-16 sm:py-20 lg:py-24 bg-slate-50 grid-pattern-light" data-testid="small-jobs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-electric font-semibold">{t.smallJobs.eyebrow}</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">{t.smallJobs.title}</h2>
            <p className="mt-3 text-base text-slate-500 max-w-xl">{t.smallJobs.subtitle}</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="small-jobs-grid">
            {t.smallJobs.items.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.05} className="h-full">
                <button
                  onClick={() => enquire(s.title)}
                  className="group w-full h-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300"
                  data-testid={`small-job-chip-${s.id}`}
                >
                  <div className="overflow-hidden aspect-square">
                    <img
                      src={IMAGES[s.img]}
                      alt={s.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <p className="p-3.5 text-xs sm:text-sm font-semibold text-navy leading-tight">{s.title}</p>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Standards strip */}
      <section className="py-14 sm:py-16 border-y border-slate-100" data-testid="standards-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-navy">{t.servicesPage.standardsTitle}</h2>
          </Reveal>
          <div className="mt-8 flex flex-wrap gap-3">
            {t.servicesPage.standards.map((s, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-navy shadow-card">
                  <ShieldCheck className="w-4 h-4 text-amber2" />
                  {s}
                </span>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <a
              href={SITE.phoneHref}
              data-testid="services-call-cta"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3 text-sm font-semibold text-white hover:bg-royal active:scale-[0.98] transition-all duration-200"
            >
              <Phone className="w-4 h-4" />
              {t.nav.call}
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
